import { z } from 'zod';
import { UserRoleSchema, type UserRole } from '@shared/schemas';
import { apiFetch } from '../client';

/**
 * Admin endpoints · wrap the `admin` edge function (slot_role_management · 10 JUL
 * 2026). ADMIN-ONLY — the edge fn 403s any non-admin caller; these wrappers just
 * shape requests/responses. `client.ts` strips `/api` and routes to
 * `<supabase>/functions/v1/admin/...`.
 *
 *   GET  /admin/users?q=            → { users, nextPage }
 *   GET  /admin/users/:id/detail    → { detail }
 *   POST /admin/set-role            → { user }
 *   POST /admin/ban                 → { user }
 *   POST /admin/reset-progress      → { ok }
 *   POST /admin/invite              → { user }
 */

export const AdminUserSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  displayName: z.string(),
  role: UserRoleSchema,
  createdAt: z.string(),
  lastSignInAt: z.string().nullable(),
  lastActiveAt: z.string().nullable(),
  emailConfirmed: z.boolean(),
  banned: z.boolean(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

export const UserDetailSchema = z.object({
  completedSprints: z.number(),
  totalSprints: z.number(),
  currentSprintId: z.string().nullable(),
});
export type UserDetail = z.infer<typeof UserDetailSchema>;

const UsersResponse = z.object({
  users: z.array(AdminUserSchema),
  nextPage: z.number().nullable(),
});
const UserResponse = z.object({ user: AdminUserSchema });
const DetailResponse = z.object({ detail: UserDetailSchema });
const OkResponse = z.object({ ok: z.boolean() });

interface SignalOpts {
  signal?: AbortSignal;
}

export const adminEndpoints = {
  /** List users + tiers + auth status · optional email/name substring filter. */
  async listUsers(
    q = '',
    opts: SignalOpts = {},
  ): Promise<{ users: AdminUser[]; nextPage: number | null }> {
    const qs = q.trim() ? `?q=${encodeURIComponent(q.trim())}` : '';
    return apiFetch(UsersResponse, `/api/admin/users${qs}`, { signal: opts.signal });
  },

  /** Progress summary for one user (lazy · card expand). */
  async getUserDetail(userId: string, opts: SignalOpts = {}): Promise<UserDetail> {
    const res = await apiFetch(
      DetailResponse,
      `/api/admin/users/${encodeURIComponent(userId)}/detail`,
      { signal: opts.signal },
    );
    return res.detail;
  },

  /** Set a user's access tier. Returns the updated row. */
  async setRole(userId: string, role: UserRole, opts: SignalOpts = {}): Promise<AdminUser> {
    const res = await apiFetch(UserResponse, '/api/admin/set-role', {
      method: 'POST',
      body: { userId, role },
      signal: opts.signal,
    });
    return res.user;
  },

  /** Block (Supabase native ban) or unblock a user. Returns the updated row. */
  async setBanned(userId: string, banned: boolean, opts: SignalOpts = {}): Promise<AdminUser> {
    const res = await apiFetch(UserResponse, '/api/admin/ban', {
      method: 'POST',
      body: { userId, banned },
      signal: opts.signal,
    });
    return res.user;
  },

  /** Wipe a user's progress (fresh start · account/tier untouched). */
  async resetProgress(userId: string, opts: SignalOpts = {}): Promise<void> {
    await apiFetch(OkResponse, '/api/admin/reset-progress', {
      method: 'POST',
      body: { userId },
      signal: opts.signal,
    });
  },

  /** Invite a new user with a preset tier. Returns the invited row. */
  async invite(email: string, role: UserRole, opts: SignalOpts = {}): Promise<AdminUser> {
    const res = await apiFetch(UserResponse, '/api/admin/invite', {
      method: 'POST',
      body: { email, role },
      signal: opts.signal,
    });
    return res.user;
  },
};
